import axios from '../axios';
import { 
  NewProjectFormData,
  NewProjectImage,
  NewSubProject,
  NewProjectService
} from '../types/newProject';

const getAuthHeaders = () => {
  const authToken = localStorage.getItem("access-Token-Edify");
  return { headers: { Authorization: `Bearer ${authToken}` } };
};

export const newProjectService: NewProjectService = {
  createProject: async (projectData: NewProjectFormData, images: NewProjectImage[]): Promise<any> => {
    const formData = new FormData();
    
    formData.append("name", projectData.name);
    formData.append("description", projectData.description);
    formData.append("startDate", projectData.startDate);
    formData.append("endDate", projectData.endDate);
    formData.append("zone", projectData.zone);
    formData.append("active", "1");
    
    images.forEach((image, index) => {
      if (image.file) {
        formData.append("images", image.file);
      }
    });

    try {
      const response = await axios.post("/proyecto/assign", formData, getAuthHeaders());
      return response.data;
    } catch (error) {
      console.error('Error al crear proyecto:', error);
      throw new Error('No se pudo crear el proyecto. Intente nuevamente.');
    }
  },

  createSubProject: async (subProject: NewSubProject, projectId: string, projectStartDate?: string, projectEndDate?: string): Promise<any> => {
    if (projectStartDate && projectEndDate) {
      const projectStart = new Date(projectStartDate);
      const projectEnd = new Date(projectEndDate);
      const subProjectStart = new Date(subProject.startDate);
      const subProjectEnd = new Date(subProject.endDate);
      
      if (subProjectStart < projectStart) {
        throw new Error('La fecha de inicio del subproyecto no puede ser anterior a la del proyecto principal');
      }
      
      if (subProjectEnd > projectEnd) {
        throw new Error('La fecha de finalización del subproyecto no puede ser posterior a la del proyecto principal');
      }
      
      if (subProjectStart > subProjectEnd) {
        throw new Error('La fecha de inicio del subproyecto no puede ser posterior a su fecha de finalización');
      }
    }
    
    const formData = new FormData();
    
    formData.append("name", subProject.name || "");
    formData.append("description", subProject.description || "");
    formData.append("proyecto", projectId);
    formData.append("startDate", subProject.startDate || "");
    formData.append("endDate", subProject.endDate || "");
    formData.append("link", subProject.link || "");

    if (subProject.subProyectoListId) {
      formData.append("skill", subProject.subProyectoListId.toString());
    } else if (subProject.rubroId) {
      formData.append("skill", subProject.rubroId.toString());
    } else {
      throw new Error('Se requiere un skill/rubro para crear el subproyecto');
    }
    
    formData.append("status", "OPEN");
    
    if (subProject.plantillaDePresupuesto) {
      formData.append("budget", subProject.plantillaDePresupuesto);
    } else {
      formData.append("budget", ""); 
    }
    
    if (subProject.computoMetrico) {
      formData.append("metricComputation", subProject.computoMetrico);
    } else {
      formData.append("metricComputation", "");
    }
    
    if (subProject.especificacionesTecnicas) {
      formData.append("technicalSpecifications", subProject.especificacionesTecnicas);
    } else {
      formData.append("technicalSpecifications", ""); 
    }
    
    if (subProject.planosArchivo) {
      formData.append("planos", subProject.planosArchivo);
    } else if (subProject.planosFile) {
      formData.append("planos", subProject.planosFile);
    } else {
      formData.append("planos", "");
    }

    try {
      const response = await axios.post("/sub_proyecto/crear", formData, getAuthHeaders());
      return response.data;
    } catch (error) {
      console.error('Error al crear subproyecto:', error);
      throw new Error('No se pudo crear el subproyecto. Intente nuevamente.');
    }
  },

  updateProject: async (projectId: string, projectData: Partial<NewProjectFormData>, images?: NewProjectImage[]): Promise<any> => {
    const formData = new FormData();
    
    if (projectData.name) formData.append("name", projectData.name);
    if (projectData.description) formData.append("description", projectData.description);
    if (projectData.startDate) formData.append("startDate", projectData.startDate);
    if (projectData.endDate) formData.append("endDate", projectData.endDate);
    if (projectData.zone) formData.append("zone", projectData.zone);
    formData.append("id", projectId);
    formData.append("active", "1");

    if (images && images.length > 0) {
      images.forEach((image) => {
        if (image.file) {
          formData.append("images", image.file);
        }
      });
    }

    try {
      const response = await axios.patch("/proyecto/update", formData, getAuthHeaders());
      return response.data;
    } catch (error) {
      console.error('Error al actualizar proyecto:', error);
      throw new Error('No se pudo actualizar el proyecto. Intente nuevamente.');
    }
  },

  updateSubProject: async (subProjectId: string, subProjectData: Partial<NewSubProject>, projectStartDate?: string, projectEndDate?: string): Promise<any> => {
    if (projectStartDate && projectEndDate && subProjectData.startDate && subProjectData.endDate) {
      const projectStart = new Date(projectStartDate);
      const projectEnd = new Date(projectEndDate);
      const subProjectStart = new Date(subProjectData.startDate);
      const subProjectEnd = new Date(subProjectData.endDate);
      
      if (subProjectStart < projectStart) {
        throw new Error('La fecha de inicio del subproyecto no puede ser anterior a la del proyecto principal');
      }
      
      if (subProjectEnd > projectEnd) {
        throw new Error('La fecha de finalización del subproyecto no puede ser posterior a la del proyecto principal');
      }
      
      if (subProjectStart > subProjectEnd) {
        throw new Error('La fecha de inicio del subproyecto no puede ser posterior a su fecha de finalización');
      }
    }
    
    const formData = new FormData();
    
    if (subProjectData.name) formData.append("name", subProjectData.name);
    if (subProjectData.description) formData.append("description", subProjectData.description);
    if (subProjectData.startDate) formData.append("startDate", subProjectData.startDate);
    if (subProjectData.endDate) formData.append("endDate", subProjectData.endDate);
    if (subProjectData.link) formData.append("link", subProjectData.link);
    
    if (subProjectData.plantillaDePresupuesto) formData.append("budget", subProjectData.plantillaDePresupuesto);
    if (subProjectData.computoMetrico) formData.append("metricComputation", subProjectData.computoMetrico);
    if (subProjectData.especificacionesTecnicas) formData.append("technicalSpecifications", subProjectData.especificacionesTecnicas);
    if (subProjectData.planosArchivo) formData.append("planos", subProjectData.planosArchivo);
    if (subProjectData.planosFile) formData.append("planos", subProjectData.planosFile);
    
    formData.append("id", subProjectId);

    try {
      const response = await axios.patch("/sub_proyecto/update", formData, getAuthHeaders());
      return response.data;
    } catch (error) {
      console.error('Error al actualizar subproyecto:', error);
      throw new Error('No se pudo actualizar el subproyecto. Intente nuevamente.');
    }
  },

  deleteSubProject: async (subProjectId: string): Promise<any> => {
    try {
      const response = await axios.delete("/sub_proyecto/delete", {
        ...getAuthHeaders(),
        data: { idSubProject: parseInt(subProjectId) }
      });
      return response.data;
    } catch (error) {
      console.error('Error al eliminar subproyecto:', error);
      throw new Error('No se pudo eliminar el subproyecto. Intente nuevamente.');
    }
  }
};

export const newProjectServiceUtils = {
  validateFileSize: (file: File, maxSize: number): boolean => {
    return file.size <= maxSize;
  },

  validateFileType: (file: File, allowedTypes: string[]): boolean => {
    return allowedTypes.includes(file.type);
  },

  fileToBase64: (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = error => reject(error);
    });
  },

  createImagePreview: (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => resolve(e.target?.result as string);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  },

  formatDateForAPI: (date: string): string => {
    const dateObj = new Date(date);
    return dateObj.toISOString().split('T')[0];
  },

  validateDateRange: (startDate: string, endDate: string): boolean => {
    const start = new Date(startDate);
    const end = new Date(endDate);
    return start < end;
  }
};

